import { Maximize2Icon } from "lucide-react";
import type { ChangeEventHandler, ComponentProps, ReactElement, ReactNode } from "react";
import { DiscussionButton } from "@/components/app/common/discussion-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { WorkspaceField } from "@/lib/mlx-workspace-client";

/*
 * The workflow tables as markup: the Runs and Results tabs' section, its
 * toolbar and notices, and the cells and pinned actions of its grid.
 * WorkflowOutputTable keeps the TanStack table, search, sorting, column
 * choice and record sheet and renders through these. The grid itself is the
 * channel table's (record-table-grid-view), its value columns at
 * fieldColumnSize (record-table-state), so fixture rows compose the same
 * table. Values arrive formatted.
 */

/** A row of a step's output, or of a tab the caller shapes itself (Runs). */
export type OutputRow = Record<string, unknown>;
/** A column: the field it shows and the path to its value in each row. */
export type OutputField = Pick<WorkspaceField, "key" | "label" | "type" | "config"> & { path: string[] };

/** What a menu or sheet trigger adds to a button: its handlers and trigger attributes. */
type TriggerProps = Omit<ComponentProps<typeof Button>, "children" | "size" | "variant">;
type WrapInMenu = (trigger: ReactElement) => ReactNode;
const atRest: WrapInMenu = (trigger) => trigger;

/*
 * Same bar as a channel section toolbar: one 40px row with the table's own
 * pickers on the left and its controls on the right, so the Results tab
 * reads like a channel data view rather than a report.
 */
export const WORKFLOW_TABLE_TOOLBAR_CLASS =
  "flex min-h-10 shrink-0 flex-wrap items-center gap-1.5 border-b border-border/60 px-3 py-1.5";

/** The pinned column of rows that are channel records: their details and discussion. */
export const WORKFLOW_RECORD_ACTIONS_COLUMN_SIZE = 56;
/** The pinned column of a table with one row action, such as opening a run on the canvas. */
export const WORKFLOW_ROW_ACTION_COLUMN_SIZE = 44;

/**
 * A workflow table: its toolbar (the caller's pickers, Search, Columns and
 * the caller's controls), its notices, then the grid, inset from the foot so
 * its last rows clear the pane's floating bar.
 */
export function WorkflowOutputTableView({
  title,
  leading,
  trailing,
  notices,
  schemaError = false,
  search = "",
  onSearchChange,
  columnsMenu = atRest,
  sheet,
  children,
}: {
  /** Names the table; Search is labelled after it. */
  title: string;
  /** Pickers that choose what the table shows, placed before the controls. */
  leading?: ReactNode;
  /** Controls placed after Search and Columns. */
  trailing?: ReactNode;
  /** Status and alerts shown between the toolbar and the grid. */
  notices?: ReactNode;
  /** The rows' dataset fields could not load, so values show unformatted. */
  schemaError?: boolean;
  search?: string;
  onSearchChange?: ChangeEventHandler<HTMLInputElement>;
  /** Wraps the Columns button in the column chooser; without one it shows at rest. */
  columnsMenu?: WrapInMenu;
  /** The record sheet, while a row's record is open. */
  sheet?: ReactNode;
  /** The grid (RecordTableGridView). */
  children?: ReactNode;
}) {
  return (
    <section
      className="flex min-h-0 min-w-0 flex-1 flex-col"
      aria-label={title}
    >
      <div className={WORKFLOW_TABLE_TOOLBAR_CLASS}>
        {leading}
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <Input
            className="w-40"
            aria-label={`Search ${title}`}
            placeholder="Search rows…"
            value={search}
            onChange={onSearchChange}
            readOnly={!onSearchChange}
          />
          {columnsMenu(<WorkflowColumnsTrigger />)}
          {trailing}
        </div>
      </div>
      {notices ? (
        <div className="shrink-0 space-y-2 px-3 pt-3">{notices}</div>
      ) : null}
      {schemaError ? (
        <p role="alert" className="shrink-0 px-3 pt-2 text-xs text-destructive">
          Dataset field definitions could not load. Values are shown without
          dataset formatting.
        </p>
      ) : null}
      {/* Bottom inset keeps the last rows clear of the pane's floating bar. */}
      <div className="flex min-h-0 flex-1 flex-col pt-2 pb-16">{children}</div>
      {sheet}
    </section>
  );
}

/** The closed column chooser; the menu's trigger props pass through. */
export function WorkflowColumnsTrigger(props: TriggerProps) {
  return (
    <Button variant="outline" {...props}>
      Columns
    </Button>
  );
}

/** The closed step details sheet, which holds the step's raw output; the sheet's trigger props pass through. */
export function WorkflowOutputDetailsTrigger(props: TriggerProps) {
  return (
    <Button variant="outline" {...props}>
      Details
    </Button>
  );
}

/** A value cell's content: one truncated line, numbers right-aligned, the formatted value on hover. */
export function WorkflowOutputCellView({
  type,
  title,
  children,
}: {
  type: OutputField["type"];
  /** The formatted value. */
  title: string;
  /** The value as the field prints it. */
  children?: ReactNode;
}) {
  return (
    <span
      className={`block truncate tabular-nums ${["number", "currency", "percent"].includes(type) ? "text-right" : "text-left"}`}
      title={title}
    >
      {children}
    </span>
  );
}

/** A row's one pinned action, such as opening a run on the canvas. */
export function WorkflowRowActionView({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  onClick?: () => void;
}) {
  return (
    <div className="flex items-center justify-center">
      <Button
        size="icon-xs"
        variant="ghost"
        aria-label={label}
        title={label}
        className="text-muted-foreground hover:text-foreground"
        onClick={onClick}
      >
        {icon}
      </Button>
    </div>
  );
}

/** A channel record's pinned actions: its details and its discussion, in the channel's record sheet. */
export function WorkflowRecordActionsView({
  title,
  onOpen,
}: {
  /** The record's title, or "record". */
  title: string;
  onOpen?: (tab: "details" | "discussion") => void;
}) {
  return (
    <div className="flex items-center justify-center gap-0.5">
      <Button
        size="icon-xs"
        variant="ghost"
        aria-label={`Open record details for ${title}`}
        title="Open record details"
        className="text-muted-foreground hover:text-foreground"
        onClick={onOpen && (() => onOpen("details"))}
      >
        <Maximize2Icon />
      </Button>
      {/* A workflow's rows don't carry their comment count, so the bubble has no badge here. */}
      <DiscussionButton
        count={0}
        label={`Open discussion for ${title}`}
        title="Open discussion"
        onClick={onOpen && (() => onOpen("discussion"))}
      />
    </div>
  );
}
